/**
 * FinanceWorkspace — Bookkeeping: upload a bank export, review what the coding memory proposed,
 * and correct it. docs/PRACTICE_FINANCE_PLAN.md Phase 1.
 *
 * Shell mirrors PermittingWorkspace.jsx exactly (ws=finance + wsTool, tray-aware via
 * `.aj-finance-ws` in outline.css, open-finance/close-finance events).
 *
 * The screen is built around one measured fact (plan §1.10.3): the work is ~100 VENDOR DECISIONS,
 * not 971 transactions. 59 unknown vendors cover 219 rows and Anthropic alone is 112 of them. So
 * the Decisions panel is the primary surface and the row list is secondary — deciding a vendor
 * once applies it everywhere and teaches the memory.
 *
 * Posting lives in ./finance/PostAndReconcile.jsx — dry run, post in two-week windows, tie out
 * after each. Until 2026-09-15 that routine existed only as server endpoints driven from a browser
 * console, which is what made this tool need Claude in the room.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { isEmbedded } from '../outline/state.js'
import { WorkspaceHeader } from './WorkspaceChrome.jsx'
import FinancesTabs from './FinancesTabs.jsx'
import PostAndReconcile from './finance/PostAndReconcile.jsx'
import ClearUndeposited from './finance/ClearUndeposited.jsx'
import ConnectPayee from './finance/ConnectPayee.jsx'
import BooksClose from './finance/BooksClose.jsx'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import {
  listBatches, getBatch, seedMemory, importBank, importPayroll, discardBatch,
  updateRow, applyToVendor, reclassify, matchCore, getChartOfAccounts,
  BUCKET_LABEL, BUCKET_GROUPS, PROJECT_ACCOUNTS,
  getExpenseItems, searchCoreProjects, setProjectCoding, clearProjectCoding,
  getExpenseMatches, linkExpenseToRow, unlinkExpenseFromRow,
  getReimbMatches, linkReimbToRow, unlinkReimbFromRow,
  getVendorStatus,
} from '../services/financeImport.js'

const STORE_KEY = 'aj:finance:ws'

function readStored() {
  try {
    const params = new URLSearchParams(window.location.search)
    const ws = params.get('ws')
    if (ws === 'finance') return { active: true }
    if (ws !== null) return null
    if (isEmbedded()) return null
    const t = sessionStorage.getItem(STORE_KEY)
    if (t) return JSON.parse(t)
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
  } catch { return null }
}

const money = (n) => (n < 0 ? '-' : '') + '$' + Math.abs(Number(n) || 0)
  .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const S = {
  body: { padding: '14px 18px 60px', fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)' },
  card: {
    border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: 14,
    background: 'var(--aj-bg-surface)', marginBottom: 14,
  },
  h: {
    fontFamily: 'Montserrat, sans-serif', textTransform: 'uppercase', fontSize: 11,
    letterSpacing: '.08em', color: 'var(--aj-text-mid)', marginBottom: 10,
  },
  btn: {
    fontFamily: 'DM Sans, sans-serif', fontSize: 13, padding: '7px 12px', borderRadius: 4,
    border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)',
    color: 'var(--aj-text)', cursor: 'pointer',
  },
  primary: { background: 'var(--aj-accent)', color: '#fff', border: '0.5px solid var(--aj-accent)' },
  mono: { fontFamily: 'DM Mono, monospace' },
  chip: (on) => ({
    fontFamily: 'DM Sans, sans-serif', fontSize: 12, padding: '5px 10px', borderRadius: 20,
    border: '0.5px solid ' + (on ? 'var(--aj-accent)' : 'var(--aj-border)'),
    background: on ? 'var(--aj-accent)' : 'var(--aj-bg-elevated)',
    color: on ? '#fff' : 'var(--aj-text)', cursor: 'pointer', whiteSpace: 'nowrap',
  }),
  input: {
    fontFamily: 'DM Mono, monospace', fontSize: 12, padding: '5px 8px', borderRadius: 4,
    border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)',
    color: 'var(--aj-text)', width: 150,
  },
  th: {
    textAlign: 'left', fontFamily: 'Montserrat, sans-serif', fontSize: 10, letterSpacing: '.06em',
    textTransform: 'uppercase', color: 'var(--aj-text-muted)', padding: '6px 8px',
    borderBottom: '0.5px solid var(--aj-border)', whiteSpace: 'nowrap',
  },
  td: { padding: '6px 8px', borderBottom: '0.5px solid var(--aj-border)', fontSize: 13, verticalAlign: 'top' },
}

function Uploader({ label, hint, onFiles, busy }) {
  const ref = useRef(null)
  return (
    <div style={{ marginBottom: 10 }}>
      <button style={{ ...S.btn }} disabled={busy} onClick={() => ref.current && ref.current.click()}>
        {label}
      </button>
      <span style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginLeft: 10 }}>{hint}</span>
      {/* multiple: Gusto caps an export at one year, and a bank export may be split too.
          Picking several at once beats making someone repeat the motion. */}
      <input
        ref={ref} type="file" accept=".csv,text/csv" multiple style={{ display: 'none' }}
        onChange={async (e) => {
          const files = Array.from(e.target.files || [])
          e.target.value = ''
          if (!files.length) return
          const payload = []
          for (const f of files) payload.push({ text: await f.text(), name: f.name })
          onFiles(payload)
        }}
      />
    </div>
  )
}

export default function FinanceWorkspace() {
  const stored = readStored()
  const [active, setActive] = useState(!!(stored && stored.active))
  const [batches, setBatches] = useState(null)
  const [batchId, setBatchId] = useState(null)
  const [data, setData] = useState(null)
  const [bucket, setBucket] = useState('unknown')
  const [chart, setChart] = useState(null)
  // The curated EX- catalogue, fetched once. Small (16) and it never changes mid-session.
  const [expItems, setExpItems] = useState(null)
  const [expMatches, setExpMatches] = useState(null)   // { byRow, linked, windowDays, error? }
  const [reimbMatches, setReimbMatches] = useState(null) // { byRow, linked, error? }
  // Vendors decided in this session. Keeps a decided row out of the queue immediately, without
  // waiting on the batch refetch that rebuilds the totals.
  const [applied, setApplied] = useState(() => new Set())
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  // Free-text filter over the row list. Correcting ONE wrong auto-coded row means finding it
  // among hundreds, and the bucket chips alone cannot do that.
  const [rowQuery, setRowQuery] = useState('')
  /**
   * Which step is on screen.
   *
   * The workspace was one long scroll: load, imports, integrity, buckets, a table of 1,186 rows,
   * then posting, then deposits. Every one of those is a different job done at a different time,
   * and stacking them made the page read as a wall rather than a sequence. Four steps, in the
   * order the work actually happens — and the order is the safety, so they are numbered.
   *
   * Not persisted: reopening on Load is the right default, because the first question is always
   * whether the data on screen is current.
   */
  const [step, setStep] = useState('load')   // load | review | post | reconcile
  // The monthly close is the front door (2026-10-28). The four steps below are the old tool, kept
  // whole behind "Look up and fix" for the rare deep repair.
  const [view, setView] = useState('close')   // close | lookup
  // Vendors Core does not recognise — Review's second kind of decision. Loaded when a batch is
  // opened, refreshed after every connection. null = not loaded yet.
  const [vendorStatus, setVendorStatus] = useState(null)
  // In a waiting-on-you bucket the list shows only what is still outstanding. Deciding a row
  // does not change which bucket it came from, so without this the chip says 4 and the list
  // underneath shows all 197 — the same contradiction one level down.
  const [showDecided, setShowDecided] = useState(false)

  useEffect(() => {
    const onOpen = () => setActive(true)
    const onClose = () => setActive(false)
    window.addEventListener('open-finance', onOpen)
    window.addEventListener('close-finance', onClose)
    return () => {
      window.removeEventListener('open-finance', onOpen)
      window.removeEventListener('close-finance', onClose)
    }
  }, [])

  useEffect(() => {
    try {
      if (active) {
        const json = JSON.stringify({ active: true })
        sessionStorage.setItem(STORE_KEY, json); localStorage.setItem(STORE_KEY, json)
      } else {
        sessionStorage.removeItem(STORE_KEY); localStorage.removeItem(STORE_KEY)
      }
    } catch { /* ignore */ }
    // Reflect the workspace in the URL, as CRM does. The activity log reads ?ws= to know which tool
    // is in use; without it, Bookkeeping time was logged as the outline and credited to whatever
    // project sat behind the workspace (Matt, 2026-11-03: a day of books showed up as Grabowski).
    try {
      const url = new URL(window.location)
      if (active) url.searchParams.set('ws', 'finance')
      else if (url.searchParams.get('ws') === 'finance') url.searchParams.delete('ws')
      window.history.replaceState(null, '', url)
    } catch { /* ignore */ }
    // The browser tab read "Alder James Projects" while Bookkeeping was open (Matt, 2026-11-02).
    // Set a frame later: OutlineView's own title effect runs after this one on mount and would put
    // "Projects" back. Closing Bookkeeping restores it.
    const wasActive = titledRef.current
    titledRef.current = active
    if (!active && !wasActive) return
    // A timeout, not a frame: a background tab paints no frames, so its title never changed.
    const id = setTimeout(() => { document.title = active ? 'Finances - Bookkeeping' : 'Alder James Projects' }, 0)
    return () => clearTimeout(id)
  }, [active])
  const titledRef = useRef(false)

  const refreshList = useCallback(async () => {
    try { const r = await listBatches(); setBatches(r.batches || []) }
    catch (e) { setErr(e.message) }
  }, [])

  const refreshBatch = useCallback(async (id, b) => {
    if (!id) { setData(null); return }
    try { setData(await getBatch(id, b === 'all' ? null : b)) }
    catch (e) { setErr(e.message) }
    // The approved company-paid expenses these bank charges may be (2026-10-28). A failed read is
    // not "no matches": it says so on the screen instead.
    try { setExpMatches(await getExpenseMatches(id)) }
    catch (e) { setExpMatches({ byRow: {}, linked: {}, error: e.message }) }
    try { setReimbMatches(await getReimbMatches(id)) }
    catch (e) { setReimbMatches({ byRow: {}, linked: {}, error: e.message }) }
  }, [])

  // Review's second queue: the vendors Core does not recognise. Loaded whenever a batch is opened
  // and after every connection. A top-level hook — the first cut nested it inside refreshBatch's
  // callback, which is an invalid hook call and took the whole workspace down (React #321).
  const refreshVendorStatus = useCallback(async (id) => {
    if (!id) { setVendorStatus(null); return }
    try { setVendorStatus(await getVendorStatus(id)) }
    catch { setVendorStatus({ recognised: 0, unrecognised: [], error: true }) }
  }, [])

  useEffect(() => { refreshVendorStatus(batchId) }, [batchId, refreshVendorStatus])

  useEffect(() => { if (active && batches === null) refreshList() }, [active, batches, refreshList])
  // Core's own chart, fetched once per session. Falls back to the memory-derived list below if
  // Core is unreachable, so a picker is never empty.
  useEffect(() => {
    if (!active || chart !== null) return
    getChartOfAccounts().then(r => setChart(r.accounts || [])).catch(() => setChart([]))
  }, [active, chart])
  useEffect(() => {
    if (!active || expItems !== null) return
    getExpenseItems().then(r => setExpItems(r.items || [])).catch(() => setExpItems([]))
  }, [active, expItems])
  useEffect(() => { if (batchId) refreshBatch(batchId, bucket) }, [batchId, bucket, refreshBatch])
  useEffect(() => { setApplied(new Set()) }, [batchId])

  if (!active) return null

  if (view === 'close') {
    return (
      <div className="aj-finance-ws">
        <WorkspaceHeader
          title="Finances"
          tabs={<FinancesTabs current="bookkeeping" />}
          actions={
            <button style={S.btn} onClick={() => window.dispatchEvent(new CustomEvent('close-finance'))}>Close</button>
          }
        />
        <BooksClose S={S} onLookup={() => setView('lookup')} ProjectCoding={ProjectCoding} ConnectPayee={ConnectPayee} />
      </div>
    )
  }

  const run = async (name, fn) => {
    setBusy(name); setErr('')
    try { return await fn() }
    catch (e) { setErr(e.message); return null }
    finally { setBusy('') }
  }

  const onSeed = () => run('seed', async () => {
    const r = await seedMemory()
    await alertDialog({
      title: 'Memory rebuilt',
      message: `Learned ${r.patterns} vendors from ${r.checks} checks in Core (${r.window}).`,
    })
  })

  // Each bank file becomes its OWN import, never merged. Merging would have to stitch the running
  // balance across files, and two overlapping or non-contiguous exports would then report a
  // confident, wrong completeness check — the one thing that check exists to prevent.
  const onBank = (files) => run('bank', async () => {
    let last = null
    for (const f of files) last = await importBank(f.text, f.name)
    await refreshList()
    if (last) { setBatchId(last.batchId); setBucket('unknown') }
    if (files.length > 1) {
      await alertDialog({
        title: `${files.length} imports created`,
        message: 'Each file is its own import, so each gets its own completeness check. '
          + 'Decisions you make on one are remembered for the others.',
      })
    }
  })

  // Payroll files accumulate. Each line is keyed by pay day, run type and person, so re-uploading
  // an overlapping export updates rather than duplicates.
  const onPayroll = (files) => run('payroll', async () => {
    let lines = 0, paydays = 0, people = 0
    for (const f of files) {
      const r = await importPayroll(f.text, f.name)
      lines += r.lines; paydays += r.paydays; people = Math.max(people, r.people)
    }
    if (batchId) await reclassify(batchId)
    await alertDialog({
      title: 'Payroll loaded',
      message: `${lines} lines across ${paydays} pay days for ${people} people.`
        + (batchId ? ' The open import has been re-checked against it.' : ''),
    })
    if (batchId) await refreshBatch(batchId, bucket)
  })

  const onRecheck = () => run('recheck', async () => {
    // FIRST ask Core what it already holds, THEN re-propose accounts. Until 2026-09-16 this button
    // only did the second half, so a payment Matt had entered into Core by hand since the last
    // dedupe stayed marked as unposted and would have been posted again. The dedupe is the one
    // check that stops the same money entering the ledger twice, and it had no button.
    const m = await matchCore(batchId)
    const r = await reclassify(batchId)
    await refreshBatch(batchId, bucket)
    await refreshVendorStatus(batchId)
    const found = (m && (m.matched ?? m.newlyMatched ?? m.marked)) ?? null
    await alertDialog({
      title: 'Re-checked',
      message: (found != null ? `${found} transaction${found === 1 ? '' : 's'} found already in Core and set aside. ` : '')
        + `${r.changed} re-proposed. ${r.kept} you had already decided were left alone.`,
    })
  })

  const onDiscard = async (id) => {
    if (!(await confirmDialog({
      title: 'Discard this import?', message: 'The staged transactions are deleted. Nothing in Core is touched.',
      confirmLabel: 'Discard', danger: true,
    }))) return
    await run('discard', async () => {
      await discardBatch(id)
      if (batchId === id) { setBatchId(null); setData(null) }
      await refreshList()
    })
  }

  const summary = (data && data.summary) || {}
  const openVendors = (data && data.openVendors) || []
  const visibleVendors = openVendors.filter(v => !applied.has(v.vendor))
  const accounts = (chart && chart.length) ? chart : ((data && data.accounts) || [])

  // What is genuinely outstanding — the number Matt was looking for and could not find.
  const waitingOnYou = ['unknown', 'historical_split', 'staff_other']
    .reduce((n, b) => n + ((summary[b] && summary[b].pending) || 0), 0)

  const rowNeedle = rowQuery.trim().toLowerCase()
  const WAITING_BUCKETS = new Set(['unknown', 'historical_split', 'staff_other'])
  const hidingDecided = WAITING_BUCKETS.has(bucket) && !showDecided
  const bucketRows = ((data && data.rows) || [])
  const decidedCount = hidingDecided
    ? bucketRows.filter(r => r.status !== 'pending').length
    : 0
  const shownRows = bucketRows.filter(r => {
    if (hidingDecided && r.status !== 'pending') return false
    if (!rowNeedle) return true
    return [r.vendor, r.description, r.account_code, r.proposed_account, r.posted_on,
            String(r.amount)].some(v => String(v || '').toLowerCase().includes(rowNeedle))
  })
  const batch = data && data.batch

  return (
    <div className="aj-finance-ws">
      <WorkspaceHeader
        title="Finances"
        titleExtra={<span style={{ fontSize: 12, color: 'var(--aj-text-mid)', marginLeft: 8 }}>Bookkeeping · Look up and fix</span>}
        tabs={<FinancesTabs current="bookkeeping" />}
        actions={
          <>
            <button style={S.btn} onClick={() => setView('close')}>Back to the monthly close</button>
            <button style={S.btn} onClick={() => window.dispatchEvent(new CustomEvent('close-finance'))}>
              Close
            </button>
          </>
        }
      />
      <div style={S.body}>
        {err && (
          <div style={{ ...S.card, borderColor: '#b4493c', color: '#b4493c' }}>{err}</div>
        )}

        {/* The four steps, in the order the work happens. Posting and Reconcile need an import
            to act on, so they say so rather than showing an empty screen. */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            ['load',      '1. Load',      'Bring in the bank export and the payroll journal'],
            ['review',    '2. Review',    'Correct what the memory proposed'],
            ['post',      '3. Post',      'Write the coded transactions into Core'],
            ['reconcile', '4. Reconcile', 'Clear undeposited funds and tie out'],
          ].map(([k, label, hint]) => (
            <button
              key={k}
              title={hint}
              style={{
                ...S.chip(step === k),
                opacity: (k === 'post' || k === 'reconcile') && !batch ? 0.45 : 1,
              }}
              onClick={() => setStep(k)}
            >
              {label}
            </button>
          ))}
        </div>

        {(step === 'post' || step === 'reconcile') && !batch && (
          <div style={S.card}>
            <div style={{ fontSize: 13, color: 'var(--aj-text-mid)' }}>
              Nothing is loaded yet. Go back to <strong>1. Load</strong> and bring in a bank export
              first — there is nothing to {step === 'post' ? 'post' : 'reconcile'} without one.
            </div>
          </div>
        )}

        {/* ── load ───────────────────────────────────────────────────── */}
        {step === 'load' && (
        <div style={S.card}>
          <div style={S.h}>Load</div>
          {/* Order on screen IS the required order. The memory must exist before a bank import can
              be classified, so it is step 1 — it was listed third once, and the only thing that
              caught it was the endpoint refusing the import. */}
          <div style={{ marginBottom: 10 }}>
            <button style={S.btn} disabled={!!busy} onClick={onSeed}>
              {busy === 'seed' ? 'Rebuilding…' : '1. Rebuild memory from Core'}
            </button>
            <span style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginLeft: 10 }}>
              Once, to start. Relearns every vendor from the firm's own coded checks; your own
              corrections are always kept.
            </span>
          </div>
          <Uploader
            label={busy === 'payroll' ? 'Reading…' : '2. Gusto payroll journal'}
            hint="Both year files at once is fine. This is what tells payroll apart from owner draws."
            onFiles={onPayroll} busy={!!busy}
          />
          <Uploader
            label={busy === 'bank' ? 'Reading…' : '3. Chase activity export'}
            hint="Date, description, amount, type and running balance. Several files become several imports."
            onFiles={onBank} busy={!!busy}
          />
        </div>

        )}

        {/* ── batches ────────────────────────────────────────────────── */}
        {step === 'load' && batches && batches.length > 0 && (
          <div style={S.card}>
            <div style={S.h}>Imports</div>
            {batches.map(b => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                <button
                  style={{ ...S.btn, ...(b.id === batchId ? S.primary : {}) }}
                  onClick={() => { setBatchId(b.id); setBucket('unknown') }}
                >
                  {b.label || b.source_filename || b.id.slice(0, 8)}
                </button>
                <span style={{ ...S.mono, fontSize: 12, color: 'var(--aj-text-muted)' }}>
                  {b.row_count} rows
                </span>
                {b.balance_ok === true && <span style={{ fontSize: 12, color: '#2e7d32' }}>✓ complete</span>}
                {b.balance_ok === false && <span style={{ fontSize: 12, color: '#b4493c' }}>⚠ gaps in the balance</span>}
                <button style={{ ...S.btn, fontSize: 12, padding: '3px 8px' }} onClick={() => onDiscard(b.id)}>
                  Discard
                </button>
              </div>
            ))}
          </div>
        )}

        {batch && step === 'review' && (
          <>
            {/* ── integrity ──────────────────────────────────────────── */}
            <div style={{
              ...S.card,
              borderColor: batch.balance_ok === false ? '#b4493c' : 'var(--aj-border)',
            }}>
              <div style={S.h}>{batch.period_start} to {batch.period_end}</div>
              <div style={{ fontSize: 13, marginBottom: 6 }}>{batch.balance_note}</div>
              <div style={{ ...S.mono, fontSize: 13, color: 'var(--aj-text-mid)' }}>
                Opens {money(batch.opening_balance)} · closes {money(batch.closing_balance)}
              </div>
              <button style={{ ...S.btn, marginTop: 10 }} disabled={!!busy} onClick={onRecheck}>
                {busy === 'recheck' ? 'Re-checking…' : 'Re-check against current memory & payroll'}
              </button>
              {batch.balance_ok === false && (
                <div style={{ fontSize: 13, color: '#b4493c', marginTop: 8 }}>
                  Rows may be missing from this export. Fix it before coding anything — a short file
                  looks perfectly normal otherwise.
                </div>
              )}
            </div>

            {/* ── buckets ────────────────────────────────────────────── */}
            <div style={{ ...S.card }}>
              <div style={S.h}>What came back</div>
              <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 4 }}>
                {waitingOnYou === 0 && !(vendorStatus && vendorStatus.unrecognised && vendorStatus.unrecognised.length)
                  ? 'Nothing is waiting on you. Every transaction has an account and a vendor Core knows.'
                  : [
                      waitingOnYou ? `${waitingOnYou} transaction${waitingOnYou === 1 ? '' : 's'} need an account` : null,
                      (vendorStatus && vendorStatus.unrecognised && vendorStatus.unrecognised.length)
                        ? `${vendorStatus.unrecognised.length} vendor${vendorStatus.unrecognised.length === 1 ? '' : 's'} Core doesn't recognise`
                        : null,
                    ].filter(Boolean).join(' · ') + '.'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 12 }}>
                Click any group below to see just those transactions in the list underneath.
              </div>
              {BUCKET_GROUPS.map(g => {
                const present = g.buckets.filter(b => summary[b])
                if (!present.length) return null
                return (
                  <div key={g.key} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{g.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 6 }}>
                      {g.blurb}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {present.map(b => {
                        // A "waiting on you" chip counts what is OUTSTANDING; everything else
                        // counts what is there. Same number for both was the whole confusion.
                        const n = g.countPending ? summary[b].pending : summary[b].count
                        if (g.countPending && !n) return null
                        return (
                          <button key={b} style={S.chip(bucket === b)} onClick={() => setBucket(b)}>
                            {BUCKET_LABEL[b] || b}
                            <span style={{ ...S.mono, marginLeft: 6, opacity: 0.85 }}>
                              {n}{g.countPending ? '' : ' · ' + money(summary[b].dollars)}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button style={S.chip(bucket === 'all')} onClick={() => setBucket('all')}>
                  Show everything
                </button>
              </div>
            </div>

            {/* ── the decisions ──────────────────────────────────────── */}
            {visibleVendors.length > 0 && (
              <div style={S.card}>
                <div style={S.h}>Decisions — {visibleVendors.length} vendors, biggest first</div>
                <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 10 }}>
                  Each one applies to every transaction for that vendor and teaches the memory, so
                  next month starts already knowing it.
                </div>
                <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 10 }}>
                  For a transaction that belongs to more than one account, type the codes separated
                  by a slash and it splits evenly — a check to both owners is{' '}
                  <span style={S.mono}>3201/3202</span>, half each.
                </div>
                {visibleVendors.slice(0, 40).map(v => (
                  <VendorDecision
                    key={v.vendor} vendor={v.vendor} count={v.count} accounts={accounts}
                    onApply={async (code, name) => {
                      setErr('')
                      try {
                        await applyToVendor(batchId, v.vendor, code, name)
                        // Drop it from the queue the moment the write lands, rather than waiting
                        // for the batch refetch — the decision is already made and the row
                        // lingering reads as a failure.
                        setApplied(prev => new Set(prev).add(v.vendor))
                        refreshBatch(batchId, bucket)     // totals catch up in the background
                      } catch (e) { setErr(e.message) }
                    }}
                  />
                ))}
              </div>
            )}

            {/* ── vendors Core does not recognise ─────────────────────── */}
            {vendorStatus && vendorStatus.unrecognised && vendorStatus.unrecognised.length > 0 && (
              <div style={S.card}>
                <div style={S.h}>
                  Vendors Core doesn't recognise — {vendorStatus.unrecognised.length}, biggest first
                </div>
                <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 10 }}>
                  A payment can't be entered until Core knows who was paid. If the vendor already
                  exists in Core under a different name, connect it here and every transaction with
                  this description will post. If it's genuinely new, create it in Core first, then
                  press Re-check above.
                </div>
                {vendorStatus.unrecognised.slice(0, 40).map(v => (
                  <div key={v.vendor} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderTop: '0.5px solid var(--aj-border)' }}>
                    <div style={{ minWidth: 260 }}>
                      <div style={{ fontSize: 13 }}>{v.vendor}</div>
                      <div style={{ ...S.mono, fontSize: 11, color: 'var(--aj-text-muted)' }}>
                        {v.rows} transaction{v.rows === 1 ? '' : 's'} · {money(v.dollars)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--aj-text-muted)' }}>bank says: {v.sample}</div>
                    </div>
                    <ConnectPayee
                      S={S} pattern={v.vendor}
                      onLinked={() => refreshVendorStatus(batchId)}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* ── rows ───────────────────────────────────────────────── */}
            <div style={S.card}>
              <div style={S.h}>
                {BUCKET_LABEL[bucket] || 'Everything'} — {shownRows.length} shown
              </div>
              {WAITING_BUCKETS.has(bucket) && (decidedCount > 0 || showDecided) && (
                <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 8 }}>
                  {hidingDecided
                    ? `${decidedCount} already decided and hidden. `
                    : 'Showing decided ones too. '}
                  <button
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: 'var(--aj-accent-blue)', font: 'inherit', textDecoration: 'underline',
                    }}
                    onClick={() => setShowDecided(v => !v)}
                  >
                    {hidingDecided ? 'Show them' : 'Hide them'}
                  </button>
                </div>
              )}
              {reimbMatches && reimbMatches.error && (
                <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>
                  Could not check payments to the team against their Reimb bills: {reimbMatches.error}
                </div>
              )}
              {expMatches && expMatches.error && (
                <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>
                  Could not check these charges against approved company-paid expenses: {expMatches.error}
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <input
                  style={{ ...S.input, width: 320 }}
                  value={rowQuery}
                  placeholder="find a transaction — vendor, description, amount"
                  onChange={e => setRowQuery(e.target.value)}
                />
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 780 }}>
                  <thead>
                    <tr>
                      <th style={S.th}>Date</th>
                      <th style={S.th}>Description</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Amount</th>
                      <th style={S.th}>Account</th>
                      <th style={S.th}>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownRows.slice(0, 400).map(r => {
                      // A row coded to a project account gets the project picker instead of being
                      // told to go and do it in Core.
                      const code = String(r.account_code || r.proposed_account || '').trim()
                      const isProject = PROJECT_ACCOUNTS.has(code)
                      return (
                      <Fragment key={r.id}>
                      <tr>
                        <td style={{ ...S.td, ...S.mono, whiteSpace: 'nowrap' }}>{r.posted_on}</td>
                        <td style={{ ...S.td, maxWidth: 380 }}>
                          <div style={{ fontWeight: 500 }}>{r.vendor || '—'}</div>
                          <div style={{ fontSize: 11, color: 'var(--aj-text-muted)' }}>
                            {String(r.description || '').slice(0, 110)}
                          </div>
                        </td>
                        <td style={{ ...S.td, ...S.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {money(r.amount)}
                        </td>
                        <td style={{ ...S.td, ...S.mono, whiteSpace: 'nowrap' }}>
                          <input
                            style={{
                              ...S.input, width: 110,
                              // A proposal is not a decision. Showing it greyed means an
                              // auto-coded row no longer reads as uncoded — which is what made
                              // a wrongly-proposed row impossible to find and correct.
                              color: r.account_code ? 'inherit' : 'var(--aj-text-muted)',
                            }}
                            defaultValue={r.account_code || r.proposed_account || ''}
                            placeholder="—"
                            title={r.account_code ? 'your decision' : 'proposed — not yet decided'}
                            onBlur={async (e) => {
                              const val = e.target.value.trim()
                              if (val === (r.account_code || '')) return
                              const wrong = unknownCodes(val, accounts)
                              if (val && wrong.length) {
                                e.target.value = r.account_code || r.proposed_account || ''
                                setErr('Not an account: ' + wrong.join(', '))
                                return
                              }
                              setErr('')
                              await updateRow(r.id, { accountCode: val, status: val ? 'accepted' : 'pending' })
                              refreshBatch(batchId, bucket)
                            }}
                          />
                          {!r.account_code && r.proposed_account && (
                            <span style={{ fontSize: 10, color: 'var(--aj-text-muted)', marginLeft: 6 }}>
                              proposed
                            </span>
                          )}
                        </td>
                        <td style={{ ...S.td, fontSize: 12, color: 'var(--aj-text-muted)', maxWidth: 320 }}>
                          {r.proposed_basis}
                          {r.core_project_name && (
                            <div style={{ marginTop: 3, color: 'var(--aj-text)' }}>
                              {r.core_project_name} · {String(r.core_item_name || '').replace(/:$/, '')}
                              {r.billable === false ? ' · absorbed' : ''}
                            </div>
                          )}
                        </td>
                      </tr>
                      {reimbMatches && (reimbMatches.linked[r.id] || reimbMatches.byRow[r.id]) && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0 }}>
                            <ReimbMatch row={r} linked={reimbMatches.linked[r.id]} match={reimbMatches.byRow[r.id]}
                              onChanged={() => refreshBatch(batchId, bucket)} />
                          </td>
                        </tr>
                      )}
                      {expMatches && (expMatches.linked[r.id] || expMatches.byRow[r.id]) && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0 }}>
                            <ExpenseMatch row={r} linked={expMatches.linked[r.id]} candidates={expMatches.byRow[r.id] || []}
                              onChanged={() => refreshBatch(batchId, bucket)} />
                          </td>
                        </tr>
                      )}
                      {isProject && !(expMatches && expMatches.linked[r.id]) && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0, borderBottom: '0.5px solid var(--aj-border)' }}>
                            <ProjectCoding
                              row={r}
                              items={expItems || []}
                              onSaved={() => refreshBatch(batchId, bucket)}
                            />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {shownRows.length > 400 && (
                <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 8 }}>
                  Showing the first 400 of {shownRows.length}. Narrow it with the search or the
                  buckets above.
                </div>
              )}
            </div>

            {/* Writing to the ledger. Everything above this line stages and codes; nothing above
                it can put a number into Core. */}
          </>
        )}

        {/* Mounted whenever an import is open and only HIDDEN on the other steps. Matt: "Every
            time I click the review step to go make an update, it clears out the post filter."
            Unmounting threw away the window, the dry run and the tie-out on every trip to Review,
            and a trip to Review is the normal thing to do between a dry run and a post. */}
        {batch && (
          <div style={{ display: step === 'post' ? undefined : 'none' }}>
            <PostAndReconcile S={S} batch={batch} postState={data.post} />
          </div>
        )}

        {batch && step === 'reconcile' && (
          <>
            <ClearUndeposited S={S} batch={batch} />
            {/* Tying out lives with posting, since it is the step that follows a post. Repeated
                here as a reminder rather than duplicated, so there is one implementation. */}
            <div style={{ ...S.card, background: 'var(--aj-bg-subtle)' }}>
              <div style={{ fontSize: 13, color: 'var(--aj-text-mid)' }}>
                The window tie-out and the whole-period check live under <strong>3. Post</strong>,
                next to the button that creates the transactions they check.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * One vendor decision.
 *
 * Applying writes to every matching row and then refetches the whole batch to rebuild the totals,
 * which takes a few seconds on 112 transactions. The first version gave no feedback at all in that
 * window, so it read as a dead button — the row eventually vanished and the work had in fact been
 * done. `working` is deliberately LOCAL to this row: it appears on the first paint after the click,
 * with no dependency on the parent's async state.
 */
function VendorDecision({ vendor, count, accounts, onApply, disabled }) {
  const [code, setCode] = useState('')
  const [working, setWorking] = useState(false)
  const listId = 'aj-fin-accts'

  const bad = unknownCodes(code, accounts)
  const parts = splitParts(code)

  const go = async () => {
    const c = code.trim()
    if (!c || working || bad.length) return
    setWorking(true)
    try { await onApply(c, nameFor(c, accounts)) } finally { setWorking(false) }
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', flexWrap: 'wrap',
      opacity: working ? 0.55 : 1, transition: 'opacity .15s ease',
    }}>
      <span style={{ minWidth: 240, fontSize: 13 }}>{vendor}</span>
      <span style={{ ...S.mono, fontSize: 12, color: 'var(--aj-text-muted)', minWidth: 64 }}>
        {count} txn{count === 1 ? '' : 's'}
      </span>
      <input
        style={S.input} value={code} placeholder="account code"
        list={listId} disabled={working || disabled}
        onChange={e => setCode(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') go() }}
      />
      <datalist id={listId}>
        <option value="3201/3202">Owner distribution — split 50/50 between Matt and Emily</option>
        {accounts.map(a => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
      </datalist>
      <button
        style={{
          ...S.btn, ...(code.trim() && !working ? S.primary : {}),
          fontSize: 12, padding: '5px 10px', minWidth: 128,
          cursor: working ? 'default' : 'pointer',
        }}
        disabled={!code.trim() || working || disabled || bad.length > 0}
        onClick={go}
      >
        {working ? `Applying to ${count}…` : `Apply to all ${count}`}
      </button>
      {!working && bad.length > 0 && (
        <span style={{ fontSize: 12, color: 'var(--aj-accent)' }}>
          not an account: {bad.join(', ')}
        </span>
      )}
      {!working && !bad.length && parts && (
        <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>
          splits evenly — {parts.join(' + ')}
        </span>
      )}
      {working && (
        <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>
          updating {count} transactions and re-totalling
        </span>
      )}
    </div>
  )
}

/**
 * A decision is USUALLY one account, but some transactions are genuinely split — a single check
 * to both owners is half a distribution to each. Those are written `3201/3202`, and the commit
 * step divides the amount evenly across the parts. Matt hit this on a paper check to himself and
 * Emily: there was no way to say it, so the row could not be cleared at all.
 *
 * Even split only. An UNEVEN split needs per-leg amounts, which belongs on the row itself, not in
 * a one-line box — deliberately not faked here.
 */
function splitParts(code) {
  const raw = String(code || '').trim()
  if (!raw.includes('/')) return null
  const parts = raw.split('/').map(x => x.trim()).filter(Boolean)
  return parts.length >= 2 ? parts : null
}

/**
 * A payment to a team member that pays back their open Reimb bills (Matt, 2026-10-28). Offered only
 * when the amount equals, to the cent, a set of their open bills. "Use it" links them; posting writes a
 * bill payment against exactly those bills, and the expenses on them read paid back.
 */
function ReimbMatch({ row, linked, match, onChanged }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const act = async (fn) => { setBusy(true); setMsg(''); try { await fn(); if (onChanged) onChanged() } catch (e) { setMsg(e.message) } finally { setBusy(false) } }
  const list = (bills) => bills.map(b => `${b.number} $${Number(b.amount).toFixed(2)}`).join(' · ')
  const box = { padding: '8px 10px', margin: '2px 0 6px', borderRadius: 6, fontSize: 12, opacity: busy ? 0.6 : 1 }
  if (linked) {
    return (
      <div style={{ ...box, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-accent-blue)' }}>
        <span style={{ fontWeight: 600 }}>Pays back {linked.person}:</span> {list(linked.bills)}
        {!row.core_record_id && (
          <button style={{ background: 'none', border: 'none', color: 'var(--aj-accent-blue)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline', marginLeft: 8 }}
            disabled={busy} onClick={() => act(() => unlinkReimbFromRow(row.id))}>unlink</button>
        )}
        <div style={{ color: 'var(--aj-text-muted)', marginTop: 3 }}>
          {row.core_record_id ? 'Posted as the bill payment. Those bills are paid off in Core.' : 'Posting this line records it in Core as the bill payment against these bills.'}
        </div>
        {msg && <div style={{ color: '#c0392b', marginTop: 3 }}>{msg}</div>}
      </div>
    )
  }
  return (
    <div style={{ ...box, background: 'rgba(30,122,79,0.06)', border: '0.5px solid rgba(30,122,79,0.35)' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 600 }}>This pays back {match.person}:</span> {match.bills.length} Reimb bill{match.bills.length === 1 ? '' : 's'}, {list(match.bills)}
        </span>
        <button style={{ ...S.btn, ...S.primary, fontSize: 12, padding: '4px 10px' }} disabled={busy}
          onClick={() => act(() => linkReimbToRow(row.id, match.bills.map(b => b.id)))}>Use it</button>
      </div>
      {msg && <div style={{ color: '#c0392b', marginTop: 3 }}>{msg}</div>}
    </div>
  )
}

/**
 * An approved COMPANY-PAID expense this bank charge may be (Matt, 2026-10-28). The team files the
 * expense in Cortex (project, phase, item, receipt); the card charge arrives here weeks later as a
 * bank line. "Use it" fills the line's project (the expense's phase), expense item and billable (the
 * item's own setting in Core) from the expense, so the check posts on the ITEMS tab and Core creates
 * the project expense entry; posting marks the expense Recorded in BQE Core. Never links on its own.
 */
function ExpenseMatch({ row, linked, candidates, onChanged }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const label = (e) => [e.filer, e.date, String(e.item || '').replace(/^EX-\s*/, ''), e.project, e.memo || e.payee].filter(Boolean).join(' · ')
  const act = async (fn) => {
    setBusy(true); setMsg('')
    try { await fn(); if (onChanged) onChanged() } catch (e) { setMsg(e.message) } finally { setBusy(false) }
  }
  const box = { padding: '8px 10px', margin: '2px 0 6px', borderRadius: 6, fontSize: 12, opacity: busy ? 0.6 : 1 }
  if (linked) {
    return (
      <div style={{ ...box, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-accent-blue)' }}>
        <span style={{ fontWeight: 600 }}>Pays an approved expense:</span>{' '}
        {linked.gone ? 'an expense that is no longer approved (it may already be in Core)' : label(linked)}
        {!row.core_record_id && (
          <button style={{ background: 'none', border: 'none', color: 'var(--aj-accent-blue)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline', marginLeft: 8 }}
            disabled={busy} onClick={() => act(() => unlinkExpenseFromRow(row.id))}>unlink</button>
        )}
        <div style={{ color: 'var(--aj-text-muted)', marginTop: 3 }}>
          {row.core_record_id ? 'Posted. The expense is recorded in Core with this check.'
            : 'Posting this line writes the check with the expense on its project; the expense is then recorded in Core.'}
        </div>
        {msg && <div style={{ color: '#c0392b', marginTop: 3 }}>{msg}</div>}
      </div>
    )
  }
  return (
    <div style={{ ...box, background: 'rgba(30,122,79,0.06)', border: '0.5px solid rgba(30,122,79,0.35)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {candidates.length === 1 ? 'This looks like an approved company-paid expense:' : `${candidates.length} approved company-paid expenses match this charge:`}
      </div>
      {candidates.map(e => (
        <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '3px 0' }}>
          <span style={{ flex: 1, minWidth: 0 }}>{label(e)}</span>
          <button style={{ ...S.btn, ...S.primary, fontSize: 12, padding: '4px 10px' }} disabled={busy}
            onClick={() => act(() => linkExpenseToRow(row.id, e.id))}>Use it</button>
        </div>
      ))}
      <div style={{ color: 'var(--aj-text-muted)', marginTop: 3 }}>Fills the project, expense item and billable from the expense.</div>
      {msg && <div style={{ color: '#c0392b', marginTop: 3 }}>{msg}</div>}
    </div>
  )
}

/**
 * Project coding for one row — shown only when the row is coded to a project account.
 *
 * These rows genuinely need LESS than an ordinary one: no account code, because the expense item
 * carries it. Pick the project and the item and Core works out that "EX- Reprographics" is 5200.
 *
 * The project search hits Core because there are ~1,500 projects, and in Core the phase IS the
 * project — "23-15-DD" is its own record, so this is one picker rather than a project and a phase.
 */
function ProjectCoding({ row, items, onSaved }) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState(null)
  // Core's project search takes a few seconds; without a word the empty box read as broken (Matt, 2026-10-29).
  const [searching, setSearching] = useState(false)
  const [project, setProject] = useState(
    row.core_project_id ? { id: row.core_project_id, name: row.core_project_name } : null)
  const [itemId, setItemId] = useState(row.core_item_id || '')
  const [billable, setBillable] = useState(row.billable !== false)
  // What it was, in words the client reads on the invoice (Matt, 2026-10-29). Without it the
  // expense goes to Core with the bank's own text ("IN *CARSTAIRS ENERGY I 805-9049048 ...").
  const [memo, setMemo] = useState(row.invoice_memo || '')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  // Search as you type, but not on every keystroke: this is a Core call and Core allows 100 a
  // minute across everything this tool does.
  useEffect(() => {
    const needle = q.trim()
    if (needle.length < 3) { setHits(null); setSearching(false); return }
    let alive = true
    setSearching(true)
    const t = setTimeout(async () => {
      try {
        const r = await searchCoreProjects(needle)
        if (alive) setHits(r.matches || [])
      } catch { if (alive) setHits([]) }
      finally { if (alive) setSearching(false) }
    }, 400)
    return () => { alive = false; clearTimeout(t) }
  }, [q])

  const item = items.find(i => i.id === itemId)

  const save = async () => {
    if (!project || !itemId || busy) return
    setBusy(true); setMsg('')
    try {
      await setProjectCoding(row.id, project.id, project.name, itemId, item ? item.name : null, billable, memo)
      setMsg('saved')
      if (onSaved) onSaved()
    } catch (e) { setMsg(e.message) } finally { setBusy(false) }
  }

  const clear = async () => {
    setBusy(true); setMsg('')
    try {
      await clearProjectCoding(row.id)
      setProject(null); setItemId(''); setMsg('cleared')
      if (onSaved) onSaved()
    } catch (e) { setMsg(e.message) } finally { setBusy(false) }
  }

  return (
    <div style={{
      padding: '8px 10px', margin: '2px 0 6px', borderRadius: 6,
      background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)',
      opacity: busy ? 0.6 : 1,
    }}>
      <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', marginBottom: 8 }}>
        This is a project cost, so it needs a project and an expense item instead of an account
        code — the item decides the account.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {project ? (
          <span style={{ ...S.mono, fontSize: 12 }}>
            {project.name}{' '}
            <button
              style={{ background: 'none', border: 'none', color: 'var(--aj-accent-blue)',
                       cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
              onClick={() => { setProject(null); setQ('') }}
            >change</button>
          </span>
        ) : (
          <input
            style={{ ...S.input, width: 300 }} value={q}
            placeholder="find the project or phase — name or number"
            onChange={e => setQ(e.target.value)}
          />
        )}

        <select
          style={{ ...S.input, width: 250, fontFamily: 'DM Sans, sans-serif' }}
          value={itemId} onChange={e => setItemId(e.target.value)}
        >
          <option value="">expense item…</option>
          {items.map(i => <option key={i.id} value={i.id}>{i.name.replace(/:$/, '')}</option>)}
        </select>

        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} />
          bill it to the client
        </label>
      </div>

      <div style={{ marginTop: 8 }}>
        <input
          style={{ ...S.input, width: '100%', boxSizing: 'border-box', fontFamily: 'DM Sans, sans-serif', fontSize: 13 }}
          value={memo} maxLength={250}
          placeholder="What it was, as the client will read it on the invoice (e.g. Title 24 energy report)"
          onChange={e => setMemo(e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>

        <button
          style={{ ...S.btn, ...(project && itemId ? S.primary : {}), fontSize: 12, padding: '5px 10px' }}
          disabled={!project || !itemId || busy}
          onClick={save}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>

        {row.core_project_id && (
          <button style={{ ...S.btn, fontSize: 12, padding: '5px 10px' }} disabled={busy} onClick={clear}>
            Not a project cost
          </button>
        )}
        {msg && <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>{msg}</span>}
      </div>

      {searching && !project && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--aj-text-muted)' }}>Searching Core for “{q.trim()}”…</div>
      )}
      {hits && !searching && !project && (
        <div style={{ marginTop: 6, maxHeight: 150, overflowY: 'auto' }}>
          {hits.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>Nothing matches.</div>
          )}
          {hits.map(p => (
            <button
              key={p.id}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none',
                       border: 'none', padding: '3px 4px', cursor: 'pointer', fontSize: 12,
                       fontFamily: 'DM Mono, monospace', color: 'var(--aj-text)' }}
              onClick={() => { setProject({ id: p.id, name: p.name }); setHits(null) }}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {!billable && (
        <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 6 }}>
          Absorbed: the cost lands on the project so its profitability is honest, but the client is
          never billed for it.
        </div>
      )}
    </div>
  )
}

function nameFor(code, accounts) {
  const list = accounts || []
  const parts = splitParts(code)
  if (parts) {
    const names = parts.map(c => {
      const hit = list.find(a => a.code === c)
      return hit ? hit.name : c
    })
    return `split evenly: ${names.join(' + ')}`
  }
  const hit = list.find(a => a.code === String(code).trim())
  return hit ? hit.name : null
}

/** Every part of a split has to be a real account, or a typo posts to nothing. */
function unknownCodes(code, accounts) {
  const list = accounts || []
  if (!list.length) return []
  const parts = splitParts(code) || [String(code || '').trim()]
  return parts.filter(c => c && !list.some(a => a.code === c))
}
